import * as THREE from 'three'
import { OrbitControls } from 'three/addons/controls/OrbitControls.js'
import { RGBELoader } from 'three/addons/loaders/RGBELoader.js'
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js'
import { DRACOLoader } from 'three/addons/loaders/DRACOLoader.js'
import { mergeVertices } from 'three/addons/utils/BufferGeometryUtils.js'
import CustomShaderMaterial from 'three-custom-shader-material/vanilla'
import GUI from 'lil-gui'
import wobbleVertexShader from './shaders/wobble/vertex.glsl'
import wobbleFragmetShader from './shaders/wobble/fragment.glsl'


/**
 * Loader
 */
const loaderElement = document.querySelector('.loader')
const progressBar = document.querySelector('.progress-bar')
const percentText = document.querySelector('.percent')

let loadingProgress = 0
let isLoaded = false

const updateProgress = (progress) => {
    loadingProgress = Math.min(progress, 99)
    progressBar.style.width = `${loadingProgress}%`
    percentText.textContent = Math.floor(loadingProgress)
}

const finishLoading = () => {
    if (isLoaded) return
    isLoaded = true

    // First go to 99%
    updateProgress(99)

    // After 1 second, go to 100% and hide loader
    setTimeout(() => {
        progressBar.style.width = '100%'
        percentText.textContent = '100'

        // Hide loader after brief pause
        setTimeout(() => {
            loaderElement.classList.add('hidden')
        }, 400)
    }, 1000)
}

/**
 * Base
 */
// Debug
const gui = new GUI({ width: 325 })
const debugObject = {}

// Canvas
const canvas = document.querySelector('canvas.webgl')

// Scene
const scene = new THREE.Scene()

// Loading Manager
const loadingManager = new THREE.LoadingManager(
    // Loaded
    () => {
        finishLoading()
    },
    // Progress
    (itemUrl, itemsLoaded, itemsTotal) => {
        const progress = (itemsLoaded / itemsTotal) * 95
        updateProgress(progress)
    }
)

// Loaders
const rgbeLoader = new RGBELoader(loadingManager)
const dracoLoader = new DRACOLoader()
dracoLoader.setDecoderPath('./draco/')
const gltfLoader = new GLTFLoader(loadingManager)
gltfLoader.setDRACOLoader(dracoLoader)

/**
 * Environment map
 */
rgbeLoader.load('./3.hdr', (environmentMap) =>
{
    environmentMap.mapping = THREE.EquirectangularReflectionMapping

    scene.background = environmentMap
    scene.environment = environmentMap
})

/**
 * Wobble
 */
debugObject.colorA = '#0000ff'
debugObject.colorB = '#ff0000'

const uniforms = {
    uTime: new THREE.Uniform(0),

    uPositionFrequency: new THREE.Uniform(0.5),
    uTimeFrequency: new THREE.Uniform(0.4),
    uStrength: new THREE.Uniform(0.3),

    uWarpPositionFrequency: new THREE.Uniform(0.38),
    uWarpTimeFrequency: new THREE.Uniform(0.12),
    uWarpStrength: new THREE.Uniform(1.7),

    uColorA: new THREE.Uniform(new THREE.Color(debugObject.colorA)),
    uColorB: new THREE.Uniform(new THREE.Color(debugObject.colorB))
}

// Material
const material = new CustomShaderMaterial({
    // CSM - CustomShaderMaterial
    baseMaterial: THREE.MeshPhysicalMaterial,
    vertexShader: wobbleVertexShader,
    fragmentShader: wobbleFragmetShader,
    uniforms: uniforms,
    silent: true,

    // MeshPhysicalMaterial
    metalness: 0,
    roughness: 0.5,
    color: '#ffffff',
    transmission: 0,
    ior: 1.5,
    thickness: 1.5,
    transparent: true,
    wireframe: false
})

// Depth for shadow
const depthMaterial = new CustomShaderMaterial({
    // CSM - CustomShaderMaterial
    baseMaterial: THREE.MeshDepthMaterial,
    vertexShader: wobbleVertexShader,
    uniforms: uniforms,
    silent: true,

    // MeshDepthMaterial
    depthPacking: THREE.RGBADepthPacking
})

// Tweaks
gui.add(uniforms.uPositionFrequency, 'value', 0, 2, 0.001).name('uPositionFrequency')
gui.add(uniforms.uTimeFrequency, 'value', 0, 2, 0.001).name('uTimeFrequency')
gui.add(uniforms.uStrength, 'value', 0, 2, 0.001).name('uStrength')

gui.add(uniforms.uWarpPositionFrequency, 'value', 0, 2, 0.001).name('uWarpPositionFrequency')
gui.add(uniforms.uWarpTimeFrequency, 'value', 0, 2, 0.001).name('uWarpTimeFrequency')
gui.add(uniforms.uWarpStrength, 'value', 0, 2, 0.001).name('uWarpStrength')

gui.addColor(debugObject, 'colorA').onChange(() => uniforms.uColorA.value.set(debugObject.colorA))
gui.addColor(debugObject, 'colorB').onChange(() => uniforms.uColorB.value.set(debugObject.colorB))

gui.add(material, 'metalness', 0, 1, 0.001)
gui.add(material, 'roughness', 0, 1, 0.001)
gui.add(material, 'transmission', 0, 1, 0.001)
gui.add(material, 'ior', 0, 10, 0.001)
gui.add(material, 'thickness', 0, 10, 0.001)
// gui.addColor(material, 'color')

// Geometry
let geometry = new THREE.IcosahedronGeometry(2.5, 50)
geometry = mergeVertices(geometry)
geometry.computeTangents()

// Mesh
const wobble = new THREE.Mesh(geometry, material)
wobble.customDepthMaterial = depthMaterial
wobble.receiveShadow = true
wobble.castShadow = true
scene.add(wobble)

// // Model
// gltfLoader.load('./suzanne.glb', (gltf) => {
//     const wobble = gltf.scene.children[0]
//     wobble.receiveShadow = true
//     wobble.castShadow = true
//     wobble.material = material
//     wobble.CustomDepthMaterial = depthMaterial

//     scene.add(wobble)
// })

/**
 * Plane
 */
const plane = new THREE.Mesh(
    new THREE.PlaneGeometry(15, 15, 1, 1),
    new THREE.MeshStandardMaterial({
    metalness: 1,
    roughness: 1,
    transmission: 0,
    ior: 1,
    thickness: 0.1,
    transparent: true,
    wireframe: false
    })
)
plane.receiveShadow = true
plane.rotation.y = Math.PI
plane.position.y = - 5
plane.position.z = 5
plane.matrixAutoUpdate = false
plane.updateMatrix()
scene.add(plane)

/**
 * Lights
 */
const directionalLight = new THREE.DirectionalLight('#ffffff', 3)
directionalLight.castShadow = true
directionalLight.shadow.mapSize.set(1024, 1024)
directionalLight.shadow.camera.far = 15
directionalLight.shadow.camera.near = 0.5
directionalLight.shadow.camera.left = -8
directionalLight.shadow.camera.right = 8
directionalLight.shadow.camera.top = 8
directionalLight.shadow.camera.bottom = -8
directionalLight.shadow.normalBias = 0.05
directionalLight.position.set(0.25, 2, - 2.25)
scene.add(directionalLight)

/**
 * Sizes
 */
const sizes = {
    width: window.innerWidth,
    height: window.innerHeight,
    pixelRatio: Math.min(window.devicePixelRatio, 2)
}

window.addEventListener('resize', () =>
{
    // Update sizes
    sizes.width = window.innerWidth
    sizes.height = window.innerHeight
    sizes.pixelRatio = Math.min(window.devicePixelRatio, 2)

    // Update camera
    camera.aspect = sizes.width / sizes.height
    camera.updateProjectionMatrix()

    // Update renderer
    renderer.setSize(sizes.width, sizes.height)
    renderer.setPixelRatio(sizes.pixelRatio)
})

/**
 * Camera
 */
// Base camera
const camera = new THREE.PerspectiveCamera(35, sizes.width / sizes.height, 0.1, 100)
camera.position.set(13, - 3, - 5)
scene.add(camera)

// Controls
const controls = new OrbitControls(camera, canvas)
controls.enableDamping = true
controls.enablePan = false
controls.minDistance = 8
controls.maxDistance = 25

/**
 * Renderer
 */
const renderer = new THREE.WebGLRenderer({
    canvas: canvas,
    antialias: true,
    powerPreference: 'high-performance',
    stencil: false
})
renderer.shadowMap.enabled = true
renderer.shadowMap.type = THREE.PCFSoftShadowMap
renderer.shadowMap.autoUpdate = false
renderer.shadowMap.needsUpdate = true
renderer.toneMapping = THREE.ACESFilmicToneMapping
renderer.toneMappingExposure = 1
renderer.setSize(sizes.width, sizes.height)
renderer.setPixelRatio(sizes.pixelRatio)
renderer.info.autoReset = false

/**
 * Visibility Change - Tab o'chirganda pause
 */
let isTabVisible = true

document.addEventListener('visibilitychange', () => {
    isTabVisible = !document.hidden
    if (isTabVisible) {
        clock.start()
        renderer.shadowMap.needsUpdate = true
    }
})

/**
 * Animate
 */
const clock = new THREE.Clock()

const tick = () =>
{
    // Tab ko'rinmasa render qilmaslik
    if (!isTabVisible) {
        window.requestAnimationFrame(tick)
        return
    }

    const elapsedTime = clock.getElapsedTime()

    // Material
    uniforms.uTime.value = elapsedTime

    // Update controls
    controls.update()

    // Render
    renderer.render(scene, camera)
    renderer.info.reset()

    // Call tick again on the next frame
    window.requestAnimationFrame(tick)
}

tick()

/**
 * Cleanup on page unload
 */
window.addEventListener('beforeunload', () => {
    renderer.dispose()
    geometry.dispose()
    material.dispose()
    depthMaterial.dispose()
})